import { CacheControl } from '@tusbar/cache-control'
import { GraphQLError } from 'graphql/error'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { NotFoundPage } from '../../../../../app/_not-found.client'
import { SeoMeta } from '../../../../../components/SeoMeta'
import {
  primeClientForReviewsProductGroupData,
  useReviewsProductGroupData,
} from '../../../../../containers/ReviewsProductGroupDataService/useReviewsProductGroupData'
import { cachePolicies } from '../../../../../lib/response-cache'
import { singleQueryParamValue } from '../../../../../lib/utils'
import { MagnoliaPage } from '../../../MagnoliaApp'

import { ReviewsPageView } from './ReviewsPageView'

export const ReviewsPage: MagnoliaPage<{
  handle: string
  writeReview: boolean
  showNotFound: boolean
}> = ({ writeReview, handle, showNotFound }) => {
  const { data } = useReviewsProductGroupData(handle)
  const seoTitle = data?.product.seoTitle?.trim().length
    ? data.product.seoTitle
    : data?.product.title

  if (showNotFound) return <NotFoundPage />

  return (
    <>
      <SeoMeta title={seoTitle} />
      <ReviewsPageView handle={handle} writeReview={writeReview} />
    </>
  )
}

ReviewsPage.getInitialProps = async function (pageContext) {
  const handle: string = singleQueryParamValue(pageContext.query.handle, '')
  const writeReview: string = singleQueryParamValue(pageContext.query['write-review'], '')

  /**
   * Prime the Apollo Client cache here since it runs during SSR and during the period where
   * the client is faded out to signify to the user we are retrieving the next page.
   *
   * Then when we are actually rendering the next page the hook that this query comes from
   * will be able to return its results instantly since it was primed.
   */
  const client = pageContext.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const { data, errors } = await primeClientForReviewsProductGroupData({ handle, client })

  if (
    pageContext.res &&
    (errors ||
      !data.product?.productGroup ||
      data.product.productGroup.productsForAvailability.length === 0)
  ) {
    pageContext.res.statusCode = 404
  }

  return {
    handle,
    responseCacheControl: cachePolicy(errors),
    writeReview: writeReview === 'true',
    showNotFound: pageContext.res?.statusCode === 404,
  }
}

function cachePolicy(errors: ReadonlyArray<GraphQLError> | undefined): CacheControl {
  return errors && errors.length > 0 ? cachePolicies.noCache : cachePolicies.publicHtmlPolicy
}
